import { useState, useRef, useEffect, useMemo } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { FormControl, SxProps, Theme, ClickAwayListener } from '@mui/material';
import {
  SelectContainer,
  StyledTextField,
  StyledInputAdornment,
  DropdownContainer,
  OptionsContainer,
  EmptyStateContainer,
  EmptyStateText,
  OptionRow,
  ColumnCell,
  CellText,
  ArrowIconStyles,
} from './styles';

export interface TableSelectColumn<T = Record<string, unknown>> {
  key: keyof T;
  label: string;
  width?: string;
  flex?: number;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

export interface TableSelectOption {
  [key: string]: unknown;
}

interface TableSelectFieldProps<T extends TableSelectOption> {
  label: string;
  options: T[];
  columns: TableSelectColumn<T>[];
  valueKey: keyof T;
  displayKey: keyof T;
  searchKeys?: (keyof T)[];
  selectedValue?: T[keyof T];
  onChange?: (value: T[keyof T], option: T) => void;
  onBlur?: () => void;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  selectStyles?: SxProps<Theme>;
  rowStyles?: SxProps<Theme>;
  placeholder?: string;
  isOptionDisabled?: (option: T) => boolean;
}

const TableSelectField = <T extends TableSelectOption>({
  label,
  options,
  columns,
  valueKey,
  displayKey,
  searchKeys,
  selectedValue,
  onChange = () => {},
  onBlur = () => {},
  disabled = false,
  required = false,
  error = false,
  selectStyles = {},
  rowStyles = {},
  placeholder,
  isOptionDisabled,
}: TableSelectFieldProps<T>) => {
  const [showMenu, setShowMenu] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);

  // Set initial display value based on selected option
  useEffect(() => {
    if (selectedValue) {
      const selectedOption = options.find((opt) => opt[valueKey] === selectedValue);
      if (selectedOption) {
        setSearchValue(String(selectedOption[displayKey] || ''));
      }
    } else {
      setSearchValue('');
    }
  }, [selectedValue, options, valueKey, displayKey]);

  const keysToSearch = searchKeys || [displayKey];

  const filteredOptions = useMemo(() => {
    if (searchValue) {
      return options.filter((option) =>
        keysToSearch.some((key) =>
          String(option[key] || '')
            .toLowerCase()
            .includes(searchValue.toLowerCase()),
        ),
      );
    }
    return options;
  }, [options, searchValue, keysToSearch]);

  const handleSelectClose = () => {
    setShowMenu(false);
    // Reset search value to selected option's display value
    if (selectedValue) {
      const selectedOption = options.find((opt) => opt[valueKey] === selectedValue);
      if (selectedOption) {
        setSearchValue(String(selectedOption[displayKey] || ''));
      }
    } else {
      setSearchValue('');
    }
    onBlur();
  };

  const handleOptionSelect = (option: T) => {
    onChange(option[valueKey], option);
    setSearchValue(String(option[displayKey] || ''));
    setShowMenu(false);
  };

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (showMenu) handleSelectClose();
      }}
    >
      <FormControl fullWidth>
        <SelectContainer ref={selectRef} sx={selectStyles}>
          <StyledTextField
            disabled={disabled}
            label={label}
            required={required}
            placeholder={placeholder}
            onClick={() => setShowMenu(true)}
            value={searchValue}
            sx={selectStyles}
            onFocus={() => setShowMenu(true)}
            onChange={(e) => setSearchValue(e.target.value)}
            InputProps={{
              endAdornment: (
                <StyledInputAdornment position="end">
                  <ArrowDropDownIcon sx={ArrowIconStyles(showMenu)} />
                </StyledInputAdornment>
              ),
            }}
            error={error}
          />

          {showMenu && (
            <DropdownContainer>
              <OptionsContainer>
                {filteredOptions.length === 0 ? (
                  <EmptyStateContainer>
                    <EmptyStateText>No options found</EmptyStateText>
                  </EmptyStateContainer>
                ) : (
                  filteredOptions.map((option, index) => {
                    const isSelected = option[valueKey] === selectedValue;
                    const isDisabled = isOptionDisabled ? isOptionDisabled(option) : false;

                    return (
                      <OptionRow
                        key={String(option[valueKey]) || index}
                        onClick={() => !isDisabled && handleOptionSelect(option)}
                        isSelected={isSelected}
                        className={isDisabled ? 'disabled' : ''}
                        sx={{
                          borderBottom:
                            index < filteredOptions.length - 1 ? '1px solid #F0F0F0' : 'none',
                          ...(rowStyles as object),
                        }}
                      >
                        {columns.map((column) => (
                          <ColumnCell
                            key={String(column.key)}
                            columnWidth={column.width}
                            columnFlex={column.flex}
                          >
                            <CellText isDisabled={isDisabled}>
                              {column.render
                                ? column.render(option[column.key], option)
                                : String(option[column.key] || '-')}
                            </CellText>
                          </ColumnCell>
                        ))}
                      </OptionRow>
                    );
                  })
                )}
              </OptionsContainer>
            </DropdownContainer>
          )}
        </SelectContainer>
      </FormControl>
    </ClickAwayListener>
  );
};

export default TableSelectField;
